import { clsx } from 'clsx'
import { useState } from 'react'
import { DayPicker } from 'react-day-picker'

import { FvButton } from '@fv/client-components'
import { fvDate, fvDateUtc } from '@fv/dates'

import { CarrierPopup } from './CarrierPopup'
import { InputGroupAddon } from './inputs/InputGroup'

const dateFormat = 'MM/DD/YYYY'

/** DayPicker works in local time, so UTC-noon dates must keep their calendar day when converted. */
export const utcToCalendarDate = (date?: Date | string | null) =>
  date ? fvDate(fvDateUtc(date).format('YYYY-MM-DD')).toDate() : undefined

type Props = {
  value?: Date
  disabled?: boolean
  pickupDate?: Date | string
  tooltip?: string
  onChange: (date?: Date) => void
}

export const CarrierDateSelector = ({
  value,
  disabled,
  pickupDate,
  tooltip = 'Expected delivery date',
  onChange,
}: Props) => {
  const [open, setOpen] = useState(false)

  const minDate = utcToCalendarDate(pickupDate)

  const handleSelect = (date?: Date) => {
    if (!date) return
    onChange(date)
    setOpen(false)
  }

  const handleClear = () => {
    onChange(undefined)
    setOpen(false)
  }

  return (
    <div className="input-group input-group--flex mb-0 basis-44">
      <InputGroupAddon
        icon="calendar-day"
        iconClassName="color-dark"
        tooltip={tooltip}
      />
      <CarrierPopup
        open={open}
        onOpenChange={nextOpen => {
          if (!disabled) setOpen(nextOpen)
        }}
        positionerProps={{
          align: 'center',
        }}
        trigger={
          <button
            type="button"
            className={clsx(
              'form-control min-w-28 text-left',
              disabled && 'opacity-50',
              !value && 'text-fv-gray-500',
            )}
            disabled={disabled}
          >
            {value ? fvDate(value).format(dateFormat) : 'mm/dd/yyyy'}
          </button>
        }
      >
        <DayPicker
          mode="single"
          numberOfMonths={1}
          selected={value}
          onSelect={handleSelect}
          disabled={minDate ? { before: minDate } : undefined}
        />
        <div className="mt-3 flex justify-end">
          <FvButton icon="times" theme="default" onClick={handleClear}>
            Clear
          </FvButton>
        </div>
      </CarrierPopup>
    </div>
  )
}
